import * as React from "react"
import { Button, Spinner, Table } from "react-bootstrap"
import { format, parseISO } from "date-fns"
import { BehaviorSubject, concat, of, Subscription } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import TablePagination from "../../TablePagination"
import PoolSelection from "../../../api/Randoms/RandomsModel/PoolSelection"
import PoolSelectionRow from "../../../api/Randoms/RandomsModel/PoolSelectionRow"
import RandomSelectionList from "../RandomSelectionList"
import PoolSelectionListGet from "../../../api/Randoms/PoolSelectionListGet"
import PoolSelectionActionButtons from "./PoolSelectionActionButtons"

interface PoolSelectionSearchParams {
  pageNumber: number
  poolId: number
}

const PageSize = 5

interface TestingPeriodBreakdownGridProps {
  poolId: number
  poolName: string
  showRandomSelectionLink?: boolean
  companyId?: number
  unmaskDonorId: number
}

interface TestingPeriodBreakdownGridState {
  loading: boolean
  data?: PoolSelection
  selectionListModal: boolean
  poolSelection?: PoolSelectionRow
}

export default class TestingPeriodBreakdownGrid extends React.PureComponent<TestingPeriodBreakdownGridProps, TestingPeriodBreakdownGridState> {
  state: TestingPeriodBreakdownGridState = {
    loading: false,
    selectionListModal: false
  }

  page$ = new BehaviorSubject<PoolSelectionSearchParams>({
    pageNumber: 1,
    poolId: 10
  })

  pageSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        PoolSelectionListGet(this.props.poolId, params.pageNumber, PageSize, this.props.companyId).pipe(
          map((result: PoolSelection) => ({ data: result, loading: false })))
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: PoolSelection }) => {
        if (newState.data) {
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      () => {
        this.setState({ loading: false })
      }
    )
  }

  componentDidUpdate(prevProps: TestingPeriodBreakdownGridProps, prevState: TestingPeriodBreakdownGridState) {
    if (prevProps.companyId !== this.props.companyId) {
      this.page$.next({ poolId: this.props.poolId, pageNumber: 1 })
    }
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  convertDate = (date: string) => {
    if (date) {
      return format(parseISO(date), "MM/dd/yyyy")
    } else {
      return ""
    }
  }

  renderRows = () => {
    if (this.state.data && this.state.data.Records && this.state.data.Records.length) {
      return this.state.data.Records.map((row: PoolSelectionRow, i: number) =>
        <tr key={i}>
          <td>
            {this.props.showRandomSelectionLink && <Button variant="link" onClick={() => this.showSelectionList(row)} className="p-0">{row.Period}</Button>}
            {!this.props.showRandomSelectionLink && row.Period}
          </td>
          <td className="text-center">{row.PoolSize}</td>
          <td className="text-center">{this.convertDate(row.SelectionDate)}</td>
          <td className="text-center border-left">{row.DrugSelected}</td>
          <td className="text-center">{row.AlcoholSelected}</td>
          <td className="text-center border-left">{row.DrugExtra}</td>
          <td className="text-center">{row.AlcoholExtra}</td>
          <td className="text-center border-left">{row.DrugCompleted}</td>
          <td className="text-center">{row.AlcoholCompleted}</td>
          <td className="text-center">{row.Status === 1 && "Canceled" }</td>
          <td className="text-center">
            <PoolSelectionActionButtons
              poolId={this.props.poolId}
              companyId={this.props.companyId || 0}
              row={row}
              onRefresh={() => this.page$.next({ poolId: this.props.poolId, pageNumber: 1 })}
            />
          </td>
        </tr>)
    } else {
      return <tr><td colSpan={11}>NO DATA FOUND</td></tr>
    }
  }

  showSelectionList = (poolSelection: PoolSelectionRow) => this.setState({ selectionListModal: true, poolSelection: poolSelection })

  hideSelectionList = () => this.setState({ selectionListModal: false, poolSelection: undefined })

  render() {
    return <>
      <h5>TESTING PERIOD BREAKDOWN</h5>
      <Table responsive className="table-sm dark">
        <thead>
          <tr className="text-normal">
            <th className="border-bottom-0">PERIOD</th>
            <th className="border-bottom-0 text-center">POOL SIZE</th>
            <th className="border-bottom-0 text-center">SELECTION DATE</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS SELECTED</th>
            <th className="border-bottom-0 text-center" colSpan={2}>EXTRA TESTS</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS COMPLETED</th>
            <th className="border-bottom-0 text-center" colSpan={2}></th>
          </tr>
          <tr className="text-normal">
            <th className="border-top-0"></th>
            <th className="border-top-0"></th>
            <th className="border-top-0"></th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">STATUS</th>
            <th className="border-top-0 text-center">ACTION</th>
          </tr>
        </thead>
        <tbody>
          {
            this.state.loading ?
              <tr>
                <td className="text-center" colSpan={11}>
                  <Spinner animation="border" variant="secondary" />
                </td>
              </tr> : this.renderRows()
          }
        </tbody>
      </Table>
      {this.state.data && <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />}
      {
        this.state.selectionListModal && this.state.poolSelection &&
        <RandomSelectionList onHide={this.hideSelectionList} show={this.state.selectionListModal} accountId={this.props.companyId}
          poolId={this.props.poolId} poolName={this.props.poolName} poolSelection={this.state.poolSelection} 
          unmaskDonorId={this.props.unmaskDonorId}/>
      }
    </>
  }
}
